import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import {
  Mail,
  User,
  CheckCircle,
  AlertCircle,
  Phone,
} from "lucide-react";

const API = process.env.REACT_APP_API_URL;

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    source: "",
    needs: [],
    message: "",
  });

  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const services = [
    "Website Development",
    "UI/UX Design",
    "SEO Optimization",
    "Paid Advertising",
    "Branding",
    "Consultation",
  ];

  const validate = () => {
    const newErrors = {};

    if (!form.name.trim()) newErrors.name = "Name is required";

    if (!form.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(form.email)) {
      newErrors.email = "Enter valid email";
    }

    if (!form.phone.trim()) {
      newErrors.phone = "Phone is required";
    } else if (!/^\d{10}$/.test(form.phone)) {
      newErrors.phone = "Enter valid 10 digit number";
    }

    if (!form.source) newErrors.source = "Select one option";
    if (form.needs.length === 0)
      newErrors.needs = "Select at least one service";
    if (!form.message.trim())
      newErrors.message = "Message required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      setLoading(true);

      await axios.post(`${API}/api/inquiries`, {
        name: form.name,
        email: form.email,
        phone: form.phone,
        source: form.source,
        services: form.needs,
        message: form.message,
      });

      setSuccess(true);

      setForm({
        name: "",
        email: "",
        phone: "",
        source: "",
        needs: [],
        message: "",
      });

      setTimeout(() => setSuccess(false), 4000);
    } catch (error) {
      console.error("Submission error:", error);
      alert("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const toggleService = (service) => {
    setForm((prev) => ({
      ...prev,
      needs: prev.needs.includes(service)
        ? prev.needs.filter((s) => s !== service)
        : [...prev.needs, service],
    }));
  };

  return (
    <section className="pt-40 pb-28 max-w-7xl mx-auto px-6">
      <div className="grid lg:grid-cols-2 gap-14">
        
        {/* LEFT SIDE */}
        <div className="space-y-10">
          <div className="p-8 rounded-3xl bg-white dark:bg-[#0B1022]
            border border-black/10 dark:border-white/15 shadow-md">
            <h2 className="text-2xl font-semibold mb-6">
              Why Work With 360Support?
            </h2>

            <ul className="space-y-4 text-slate-600 dark:text-slate-400">
              {[
                "Transparent communication & realistic timelines",
                "Scalable, future-ready technical solutions",
                "Design-driven user experience",
                "Performance & growth focused strategy",
              ].map((item, i) => (
                <li key={i} className="flex gap-3">
                  <CheckCircle size={18} className="text-blue-600 mt-1" />
                  {item}
                </li>
              ))}
            </ul>
          </div>

          <div className="p-8 rounded-3xl bg-white dark:bg-[#0B1022]
            border border-black/10 dark:border-white/15 shadow-md">
            <h3 className="text-xl font-semibold mb-4">
              Direct Contact
            </h3>
            <div className="flex items-center gap-3 text-blue-600 font-medium">
              <Mail size={20} />
              360supp0rt.agency@gmail.com
            </div>
          </div>
        </div>

        {/* RIGHT SIDE FORM */}
        <motion.form
          onSubmit={handleSubmit}
          initial={{ opacity: 0, x: 30 }}
          animate={{ opacity: 1, x: 0 }}
          className="p-8 rounded-3xl bg-white dark:bg-[#0B1022]
            border border-black/15 dark:border-white/25 shadow-xl"
        >
          <h2 className="text-2xl font-semibold mb-6">
            Start Your Project
          </h2>

          <InputField
            label="Full Name"
            icon={User}
            value={form.name}
            error={errors.name}
            onChange={(e) =>
              setForm({ ...form, name: e.target.value })
            }
          />

          <InputField
            label="Email"
            icon={Mail}
            type="email"
            value={form.email}
            error={errors.email}
            onChange={(e) =>
              setForm({ ...form, email: e.target.value })
            }
          />

          <InputField
            label="Phone Number"
            icon={Phone}
            value={form.phone}
            error={errors.phone}
            onChange={(e) =>
              setForm({ ...form, phone: e.target.value })
            }
          />

          {/* Source */}
          <div className="mb-6">
            <label className="block mb-2 font-medium">
              How did you find us?
            </label>
            <select
              value={form.source}
              onChange={(e) =>
                setForm({ ...form, source: e.target.value })
              }
              className="w-full p-3 rounded-xl border border-black/15
              dark:border-white/25 bg-transparent"
            >
              <option value="">Select one</option>
              <option>Instagram</option>
              <option>LinkedIn</option>
              <option>WhatsApp Ad</option>
              <option>YouTube Ad</option>
              <option>Direct Website</option>
              <option>Others</option>
            </select>
            {errors.source && <ErrorText message={errors.source} />}
          </div>

          {/* Services */}
          <div className="mb-6">
            <label className="block mb-3 font-medium">
              Services Required
            </label>
            <div className="grid grid-cols-2 gap-3">
              {services.map((service) => (
                <label key={service} className="flex gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={form.needs.includes(service)}
                    onChange={() => toggleService(service)}
                  />
                  {service}
                </label>
              ))}
            </div>
            {errors.needs && <ErrorText message={errors.needs} />}
          </div>

          {/* Message */}
          <div className="mb-6">
            <label className="block mb-2 font-medium">
              Project Details
            </label>
            <textarea
              rows="4"
              value={form.message}
              onChange={(e) =>
                setForm({ ...form, message: e.target.value })
              }
              className="w-full p-3 rounded-xl border border-black/15
              dark:border-white/25 bg-transparent"
            />
            {errors.message && <ErrorText message={errors.message} />}
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 rounded-xl font-semibold text-white
            bg-gradient-to-r from-blue-600 to-indigo-600
            hover:scale-[1.02] transition disabled:opacity-70"
          >
            {loading ? "Submitting..." : "Submit Inquiry"}
          </button>

          {success && (
            <p className="mt-4 text-green-600 flex gap-2">
              <CheckCircle size={16} />
              Message sent successfully
            </p>
          )}
        </motion.form>
      </div>
    </section>
  );
}

/* Input Component */
function InputField({ label, icon: Icon, type = "text", value, onChange, error }) {
  return (
    <div className="mb-6">
      <label className="block mb-2 font-medium">{label}</label>
      <div className="relative">
        <Icon className="absolute left-3 top-3 text-slate-400" size={18} />
        <input
          type={type}
          value={value}
          onChange={onChange}
          className="w-full pl-10 pr-4 py-3 rounded-xl
          border border-black/15 dark:border-white/25 bg-transparent"
        />
      </div>
      {error && <ErrorText message={error} />}
    </div>
  );
}

function ErrorText({ message }) {
  return (
    <p className="text-sm text-red-500 mt-1 flex items-center gap-1">
      <AlertCircle size={14} />
      {message}
    </p>
  );
}